﻿import * as $ from 'jquery';
import * as rxui from 'common/rxui/core';
import { Observable, Subject, BehaviorSubject } from 'common/barrels/rx';
import { serialDisposable, disposableSubscription, disposable, IDisposable } from 'common/barrels/core';
import { bind, Binding } from 'common/dom/binding';
import { div } from 'common/barrels/dom';
import 'common/styles/controls/listbox.less';

const maxListHeight = 300;
const listMargin = 20;
const navKeys = { 40: 1, 38: -1 };

export function listbox(
    value: Binding<string>,
    getListItems: (filter: string) => Observable<ListItem[]>,
    getListItemByDisplay: (display: string) => Observable<ListItem>,
    getListItemByKey: (key: string) => Observable<ListItem>
): JQuery {
    const $element = div('studio-listbox');

    const control = new ListBox($element, getListItems, getListItemByDisplay, getListItemByKey);

    bind<string>(value, x => control.setValues.next(x), () => control.controlVales);

    return $element.owns(control);
}


export type ListItem = { keyValue: string, displayValue: string };
class ListResult { constructor(public filter: string, public items: ListItem[]) { } }

class ListBox implements IDisposable {
    private value = new BehaviorSubject<ListItem>(null);
    public setValues = new Subject<string>();
    private listDisposable = serialDisposable(null);
    private $input: JQuery;
    private $icon: JQuery;
    private listOpen = false;
    private readyTrigger = new Subject<any>();
    private updating = false;
    private focusGroup: BehaviorSubject<JQuery>;
    private subscriptions: IDisposable;
    private minimumFilterLength = 0;
    public controlVales = new Subject<string>();

    constructor(public $element: JQuery, public getListItems: (filter: string) => Observable<ListItem[]>, getListItemByDisplay: (display: string) => Observable<ListItem>, getListItemByKey: (key: string) => Observable<ListItem>, public enabled: Observable<boolean> = Observable.of(true), populationDelay: number = 25) {
        this.$element = $element;
        this.$element.addClass('control-container studio-listbox');
        this.$input = $('<input type="text" />').appendTo(this.$element);

        this.focusGroup = new BehaviorSubject<JQuery>(this.$input);

        this.$icon = $('<i />').addClass('fa fa-long-arrow-down').appendTo(this.$element);

        var valueSet = this.setValues.withLatestFrom(this.value, (x, y) => ({ newKey: x, currentItem: y })).share();

        var valueChanged = rxui.valueChanged(this.$input, this.focusGroup.switchMap(x => rxui.groupBlur(x))).share();

        var input = rxui.from(this.$input, 'propertychange input').filter(() => !this.updating).share();

        this.subscriptions = disposableSubscription(
            Observable.fromEvent(this.$input, 'focusin')
                .switchMap(() => input
                    .map(() => this.$input.val())
                    .distinctUntilChanged()
                    .map(() => true)
                    .debounceTime(populationDelay)
                    .merge(rxui.filteredKeys(this.$input, [$.ui.keyCode.DOWN]).filter(() => !this.listOpen).map(() => true))
                    .filter(() => this.$input.val().length >= this.minimumFilterLength)
                    .takeUntil(Observable.fromEvent(this.$input, 'focusout'))
                )
                .map(() => this.$input.val())
                .switchMap(x => this.getListItems(x).map(y => new ListResult(x, y)))
                .subscribe(x => this.renderList(x)),

            rxui.mousedownleft(this.$icon)
                .do(e => e.preventDefault())
                .whereLatest(this.enabled)
                .filter(() => this.listOpen === false)
                .do(() => this.$input.focus())
                .filter(() => this.$input.val().length >= this.minimumFilterLength)
                .map(() => this.$input.val())
                .switchMap(x => this.getListItems(x).map(y => new ListResult(x, y)))
                .subscribe(x => this.renderList(x)),

            input.map(() => this.$input.val()).filter(x => x.length < this.minimumFilterLength && this.listOpen).subscribe(() => this.closeList()),
            valueChanged.filter(x => x === '').subscribe(() => this.select(null, true)),

            valueChanged
                .filter(x => x !== '')
                // this is to ensure this runs after the menu handler, which may save us a server roundtrip
                .flatMap(x => Observable.interval(0).first().map(() => x))
                .withLatestFrom(this.value, (x, y) => ({ newDisplay: x, currentItem: y }))
                .filter(x => x.currentItem === null || x.newDisplay !== x.currentItem.displayValue)
                .switchMap(x => this.isCaseInsensitiveMatch(x.newDisplay, x.currentItem) ? Observable.of(x.currentItem) : getListItemByDisplay(x.newDisplay))
                .withLatestFrom(this.value, (x, y) => ({ newValue: x, oldValue: y }))
                .subscribe(x => this.select(x.newValue, !this.itemsEquivalent(x.oldValue, x.newValue))),

            valueSet.filter(x => x.newKey === null).subscribe(() => this.select(null, false)),
            valueSet.filter(x => x.newKey !== null && (x.currentItem === null || x.newKey !== x.currentItem.keyValue))
                .switchMap(x => getListItemByKey(x.newKey))
                .subscribe(x => this.select(x, false)),

            this.value.distinctUntilChanged().subscribe(x => this.controlVales.next(x === null ? null : x.keyValue)),
            this.listDisposable,
            Observable.interval(0).first().subscribe(() => this.readyTrigger.next(null)),
        );
    }

    dispose() {
        this.subscriptions.dispose();
        this.listDisposable.dispose();
    }

    private isCaseInsensitiveMatch(newDisplayValue: string, currentItem: ListItem) {
        return currentItem !== null && newDisplayValue !== null && newDisplayValue.toLowerCase() === currentItem.displayValue.toLowerCase();
    }

    private itemsEquivalent(a: ListItem, b: ListItem) {
        return a === null && b === null ? true
             : a === null ? false
             : b === null ? false
             : a.keyValue === b.keyValue;
    }

    private makeListItem(item: ListItem) {
        return $('<li />').text(item.displayValue).data('ListItem', item);
    }

    private makeList(items: ListItem[]) {
        return items.reduce((a, x) => a.append(this.makeListItem(x)), $('<ul />'));
    }

    private renderList(result: ListResult) {
        const items = result.items;

        if (items.length === 0) {
            this.closeList();
            return;
        }

        var $list = this.makeList(items);

        var offset = this.$element.offset();
        var availableAbove = offset.top - listMargin;
        var availableBelow = $(window).height() - offset.top - this.$element.height() - listMargin;
        var direction = availableBelow > maxListHeight ? 'down'
            : availableAbove > availableBelow ? 'up'
                : 'down';

        var maxHeight = Math.min(direction === 'up' ? availableAbove : availableBelow, maxListHeight);

        var $popupInner = $('<div />').append($('<div />').append($list)).css('max-height', maxHeight + 'px');

        var $popup = $('<div />').append($popupInner).addClass('studio-listbox-list')
            .css('min-width', this.$element.width())
            .appendTo(this.$element.closest('.popup-container'))
            .position({
                my: 'left ' + (direction === 'down' ? 'top' : 'bottom'),
                at: 'left ' + (direction === 'down' ? 'bottom' : 'top'),
                of: this.$element,
                collision: 'flipfit none'
            });

        this.focusGroup.next(this.$input.add($list).add($popup).add($popupInner));

        this.listOpen = true;

        var selection = new BehaviorSubject<ListItem>(null);

        var selectionClass = 'studio-listbox-selected';

        this.listDisposable.set(disposableSubscription(
            disposable(() => $popup.remove()),

            rxui.clickOutside($list.add(this.$element)).subscribe(() => this.closeList()),

            rxui.groupBlur(this.$input.add($list).add($popup).add($popupInner))
                .withLatestFrom(selection.skip(1), (x, y) => y)
                .withLatestFrom(this.value, (x, y) => ({ newValue: x, oldValue: y }))
                .subscribe(x => {
                    if (this.itemsEquivalent(x.oldValue, x.newValue)) {
                        this.closeList();
                        return;
                    }

                    if (x !== null) {
                        this.select(x.newValue, true);
                    }
                    else {
                        var selectedValue = items.filter(y => (y.displayValue === result.filter) || (y.displayValue.toLowerCase() === result.filter.toLowerCase()))[0] || null;
                        this.select(selectedValue, true);
                    }
                }
            ),

            rxui.filteredKeys(this.$input, [$.ui.keyCode.ESCAPE]).subscribe(e => {
                this.setText(result.filter);
                this.closeList();
                e.stopPropagation();
            }),

            rxui.key(this.$input).map(x => navKeys[x]).filter(x => x !== undefined)
                .withLatestFrom(selection.asObservable(), (x, y) => ({ nav: x, current: y }))
                .subscribe(x => {
                    var currentIndex = x.current === null ? 0 : items.indexOf(x.current) + 1;
                    const continuum = [items[items.length - 1], null, ...items, null];
                    const newSelection = continuum[currentIndex + 1 + x.nav];
                    selection.next(newSelection);
                }),

            selection.pairwise().subscribe(x => {
                if (x[0] !== null)
                    $list.find('li').toArray().map(y => $(y)).filter(y => y.data('ListItem') === x[0])[0].removeClass(selectionClass);

                if (x[1] != null) {
                    var $item = $list.find('li').toArray().map(y => $(y)).filter(y => y.data('ListItem') === x[1])[0];
                    $item.addClass(selectionClass);

                    this.scrollItemIntoView($item, $popup);
                }

                this.setText(x[1] === null ? result.filter : x[1].displayValue);
            }),

            rxui.mousedownleft($popup.find('ul'))
                .map(x => $(x.target))
                .map(x => <ListItem>(x.data('ListItem')))
                .race(rxui.filteredKeys(this.$input, [13]).do(k => k.stopPropagation()).withLatestFrom(selection.asObservable(), (x, y) => y))
                .subscribe(x => this.select(x, true)),

            rxui.scrollOutside($popup).first().subscribe(() => this.closeList()),

            selection
        ));
    }

    private scrollItemIntoView($item: JQuery, $popup: JQuery) {
        var st = $popup.children('div')[0].scrollTop;

        if ($item.position().top + $item.height() > st + $popup.height())
            $item[0].scrollIntoView(false);
        else if (st > $item.position().top)
            $item[0].scrollIntoView(true);
    }

    private setText(text: string) {
        this.updating = true;
        this.$input.val(text);
        this.updating = false;
    }

    private select(item: ListItem, raiseSelected: boolean) {
        this.setText(item === null ? '' : item.displayValue);
        this.value.next(item);
        this.closeList();
    }

    private closeList() {
        this.listDisposable.set(null);
        this.listOpen = false;
        this.focusGroup.next(this.$input);
    }
}